import {
  RemoteAudioTrack,
  Room,
  RoomEvent,
  Track,
  type RemoteTrack,
} from 'livekit-client';
import { loadCharacterAsset } from './asset';
import { AvatarLiveError } from './errors';
import { TypedEmitter } from './events';
import { AudioLipSync } from './lip-sync';
import {
  assertCharacterAssetParamSchema,
  CLIENT_METRICS_TOPIC,
  COMMAND_TOPIC,
  decodeDriveFrame,
  EVENT_TOPIC,
  LEGACY_PARAM_SCHEMA_VERSION,
  PARAM_TOPIC,
  readParamSchemaVersion,
  SUPPORTED_PARAM_SCHEMA_VERSIONS,
  supportsParamSchemaVersion,
} from './protocol';
import { AvatarRenderer } from './renderer';
import type {
  AvatarEvents,
  AvatarLiveOptions,
  AvatarState,
  DegradationAudio,
  RenderMode,
  RequestedRenderMode,
  SessionResponse,
  TurnMetrics,
  VideoFit,
} from './types';

// Simli can spend up to its configured 30s startup window before publishing a
// participant. Leave room for the control-plane round trip and browser event
// delivery instead of disconnecting the room while the vendor is still joining.
const SESSION_READY_TIMEOUT_MS = 45_000;
const RENDER_MODE_CHANGE_TIMEOUT_MS = 15_000;
const INTERRUPTION_MODE_CHANGE_TIMEOUT_MS = 5_000;
const WEBGL_CONTEXT_RECOVERY_TIMEOUT_MS = 2_000;
type SessionEndReason = 'user' | 'connect_failed' | 'unexpected_disconnect';
const AVATAR_STATES = new Set<AvatarState>([
  'disconnected',
  'connecting',
  'idle',
  'listening',
  'thinking',
  'speaking',
]);
const DEGRADATION_AUDIO = new Set<DegradationAudio>([
  'available',
  'prerecorded',
  'tts_fallback',
  'unavailable',
]);

class LifecycleCancelledError extends Error {
  constructor() {
    super('connection lifecycle was cancelled');
    this.name = 'AbortError';
  }
}

interface SessionReadyWaiter {
  generation: number;
  sessionId: string;
  paramSchemaVersion?: number;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

interface RenderModeWaiter {
  generation: number;
  requestId: string;
  promise: Promise<RenderMode>;
  resolve: (renderMode: RenderMode) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

interface InterruptionModeWaiter {
  generation: number;
  requestId: string;
  enabled: boolean;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
}

export class AvatarLive {
  private readonly emitter = new TypedEmitter<AvatarEvents>();
  private room?: Room;
  private renderer?: AvatarRenderer;
  private audioElement?: HTMLAudioElement;
  private lipSync?: AudioLipSync;
  private videoElement?: HTMLVideoElement;
  private serverVideoFit: VideoFit = 'cover';
  private canvas?: HTMLCanvasElement;
  private currentState: AvatarState = 'disconnected';
  private selectedRenderMode?: RenderMode;
  private forcedRenderMode?: RenderMode;
  private contextFailures = 0;
  private contextRecoveryTimeout?: ReturnType<typeof setTimeout>;
  private fallbackInProgress = false;
  private microphoneStarted = false;
  private microphoneDeviceId?: string;
  private readonly remoteAudioTracks = new Set<RemoteAudioTrack>();
  private activeAudioOutputDeviceId?: string;
  private lifecycleGeneration = 0;
  private connectPromise?: Promise<void>;
  private disconnectPromise?: Promise<void>;
  private connectAbortController?: AbortController;
  private readonly sessions = new Map<string, SessionResponse>();
  private readonly sessionDeletePromises = new Map<string, Promise<void>>();
  private readyWaiter?: SessionReadyWaiter;
  private renderModeWaiter?: RenderModeWaiter;
  private renderModeRequestSequence = 0;
  private interruptionModeWaiter?: InterruptionModeWaiter;
  private interruptionModeRequestSequence = 0;
  private sessionReady = false;
  private paramSchemaVersion = LEGACY_PARAM_SCHEMA_VERSION;
  private pendingServerState?: AvatarState;
  private stateBeforeReconnect?: AvatarState;
  private readonly intentionalDisconnects = new WeakSet<Room>();
  private readonly connectingRooms = new WeakSet<Room>();
  private lastSessionExpiresAtMs?: number;

  constructor(private readonly options: AvatarLiveOptions) {
    if (!options.sessionEndpoint) throw new TypeError('sessionEndpoint is required');
    if (!options.container) throw new TypeError('container is required');
  }

  get state(): AvatarState {
    return this.currentState;
  }

  on<E extends keyof AvatarEvents>(event: E, callback: AvatarEvents[E]): () => void {
    return this.emitter.on(event, callback);
  }

  async connect(): Promise<void> {
    if (this.disconnectPromise) await this.disconnectPromise;
    if (this.connectPromise) return this.connectPromise;
    if (this.currentState !== 'disconnected') return;
    this.requireUserActivation();
    const generation = ++this.lifecycleGeneration;
    this.setState('connecting');
    const promise = this.connectInternal(generation);
    this.connectPromise = promise;
    try {
      await promise;
    } finally {
      if (this.connectPromise === promise) this.connectPromise = undefined;
    }
  }

  async disconnect(): Promise<void> {
    if (this.disconnectPromise) return this.disconnectPromise;
    ++this.lifecycleGeneration;
    this.connectAbortController?.abort();
    this.cancelContextRecoveryTimeout();
    this.cancelSessionReady(new LifecycleCancelledError());
    this.cancelRenderModeChange(new LifecycleCancelledError());
    this.cancelInterruptionModeChange(new LifecycleCancelledError());
    const pendingConnect = this.connectPromise;
    const promise = (async () => {
      await Promise.resolve();
      const cleanup = this.disconnectInternal();
      this.setState('disconnected');
      await cleanup;
      await pendingConnect?.catch(() => undefined);
      await this.disconnectInternal();
    })();
    this.disconnectPromise = promise;
    try {
      await promise;
    } finally {
      if (this.disconnectPromise === promise) this.disconnectPromise = undefined;
    }
  }

  private async connectInternal(generation: number): Promise<void> {
    this.sessionReady = false;
    this.paramSchemaVersion = LEGACY_PARAM_SCHEMA_VERSION;
    this.pendingServerState = undefined;
    const abortController = new AbortController();
    this.connectAbortController = abortController;
    try {
      const requestedMode = this.forcedRenderMode ?? this.resolveRenderMode(this.options.renderMode ?? 'auto');
      let session = await this.createSession(requestedMode);
      this.rememberSessionExpiry(session);
      this.sessions.set(session.session_id, session);
      this.assertCurrentGeneration(generation);
      this.selectedRenderMode = session.render_mode;
      try {
        await this.prepareElements(session, abortController.signal);
      } catch (error) {
        if (
          session.render_mode !== 'client'
          || !(error instanceof AvatarLiveError)
          || error.code !== 'WEBGL_UNAVAILABLE'
        ) throw error;

        this.emitter.emit('error', error);
        await this.releaseSession(session, 'connect_failed');
        this.clearPreparedElements();
        this.assertCurrentGeneration(generation);
        this.forcedRenderMode = 'server';
        session = await this.createSession('server');
        this.rememberSessionExpiry(session);
        this.sessions.set(session.session_id, session);
        this.assertCurrentGeneration(generation);
        this.selectedRenderMode = session.render_mode;
        await this.prepareElements(session, abortController.signal);
      }
      this.assertCurrentGeneration(generation);
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        webAudioMix: true,
        audioCaptureDefaults: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      this.room = room;
      const ready = this.createSessionReadyWaiter(
        generation,
        session.session_id,
        session.render_mode === 'client' ? this.paramSchemaVersion : undefined,
      );
      this.installRoomHandlers(room, generation);
      this.connectingRooms.add(room);
      try {
        await room.connect(session.livekit_url, session.token, { autoSubscribe: true });
      } finally {
        this.connectingRooms.delete(room);
      }
      this.assertCurrentGeneration(generation);
      await ready;
      this.assertCurrentGeneration(generation);
      if (this.currentState === 'connecting') this.setState('idle');
    } catch (error) {
      const cancelled = this.isLifecycleCancellation(error) || generation !== this.lifecycleGeneration;
      const cleanup = this.disconnectInternal(cancelled ? 'user' : 'connect_failed');
      this.setState('disconnected');
      await cleanup;
      if (cancelled) throw new LifecycleCancelledError();
      const avatarError = this.asAvatarError(error);
      this.emitter.emit('error', avatarError);
      throw avatarError;
    } finally {
      if (this.connectAbortController === abortController) this.connectAbortController = undefined;
    }
  }

  async startMic(deviceId?: string): Promise<void> {
    if (!this.room) throw this.notConnectedError();
    try {
      await this.room.localParticipant.setMicrophoneEnabled(true, {
        deviceId,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      });
      this.microphoneStarted = true;
      this.microphoneDeviceId = deviceId;
    } catch (error) {
      const denied = error instanceof DOMException && ['NotAllowedError', 'PermissionDeniedError'].includes(error.name);
      const avatarError = new AvatarLiveError(
        denied ? 'MIC_DENIED' : 'UPSTREAM_ERROR',
        denied ? '麦克风权限被拒绝，请在浏览器设置中允许访问' : String(error),
        false,
      );
      this.emitter.emit('error', avatarError);
      throw avatarError;
    }
  }

  /** Resume browser audio playback from a real click/tap after autoplay was blocked. */
  async resumeAudio(): Promise<void> {
    if (!this.room) throw this.notConnectedError();
    try {
      await this.room.startAudio();
      await this.audioElement?.play();
      await this.lipSync?.resume();
    } catch (error) {
      const avatarError = new AvatarLiveError(
        'AUTOPLAY_BLOCKED',
        '浏览器阻止了音频播放，请点击页面后重试',
        false,
      );
      this.emitter.emit('error', avatarError);
      throw avatarError;
    }
  }

  stopMic(): void {
    this.microphoneStarted = false;
    this.microphoneDeviceId = undefined;
    void this.room?.localParticipant.setMicrophoneEnabled(false);
  }

  setMicMuted(muted: boolean): void {
    for (const publication of this.room?.localParticipant.audioTrackPublications.values() ?? []) {
      if (publication.source !== Track.Source.Microphone || !publication.audioTrack) continue;
      void (muted ? publication.audioTrack.mute() : publication.audioTrack.unmute());
    }
  }

  async say(text: string): Promise<void> {
    const normalized = text.trim();
    if (!normalized) return;
    await this.sendCommand({ type: 'say', text: normalized });
  }

  interrupt(): void {
    void this.sendCommand({ type: 'interrupt' }).catch((error) => {
      this.emitError(this.asAvatarError(error));
    });
  }

  async setInterruptionsEnabled(enabled: boolean): Promise<void> {
    if (typeof enabled !== 'boolean') throw new TypeError('enabled must be a boolean');
    if (!this.room) throw this.notConnectedError();

    const generation = this.lifecycleGeneration;
    const requestId = `interruptions-${Date.now()}-${++this.interruptionModeRequestSequence}`;
    const changed = this.createInterruptionModeWaiter(generation, requestId, enabled);
    try {
      await this.sendCommand({
        type: 'setInterruptions',
        enabled,
        requestId,
      });
      await changed;
      this.assertCurrentGeneration(generation);
    } catch (error) {
      const avatarError = this.asAvatarError(error);
      this.cancelInterruptionModeChange(avatarError, requestId);
      throw avatarError;
    }
  }

  private async createSession(renderMode: RenderMode): Promise<SessionResponse> {
    const response = await fetch(this.options.sessionEndpoint, {
      method: 'POST',
      // Do not bind session creation to the lifecycle AbortController. The server may commit
      // the POST before an abort discards its response, leaving no session_id to DELETE. A
      // cancelled lifecycle instead waits for the response, records the id, and releases it.
      keepalive: true,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...this.options.sessionHeaders,
      },
      body: JSON.stringify({
        character_id: this.options.characterId,
        voice_id: this.options.voiceId,
        system_prompt_id: this.options.systemPromptId,
        render_mode: renderMode,
        locale: this.options.locale ?? 'zh-CN',
        metadata: this.options.metadata ?? {},
      }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => undefined) as { error?: { code?: string; message?: string; fatal?: boolean } } | undefined;
      throw new AvatarLiveError(
        body?.error?.code ?? (response.status === 429 ? 'RATE_LIMITED' : 'UPSTREAM_ERROR'),
        body?.error?.message ?? `创建会话失败：${response.status}`,
        body?.error?.fatal ?? response.status >= 400,
      );
    }
    return response.json() as Promise<SessionResponse>;
  }

  private async prepareElements(session: SessionResponse, signal: AbortSignal): Promise<void> {
    this.options.container.replaceChildren();
    this.serverVideoFit = session.video_fit ?? 'cover';
    this.audioElement = document.createElement('audio');
    this.audioElement.autoplay = true;
    // Output audio is audible by default. Browsers may still require the existing
    // click/tap activation path before unmuted autoplay is permitted.
    this.audioElement.defaultMuted = false;
    this.audioElement.muted = false;
    this.audioElement.volume = 1;
    this.audioElement.style.display = 'none';
    this.options.container.append(this.audioElement);

    if (session.render_mode === 'client') {
      if (!session.character) throw new Error('client rendering session did not include a character asset');
      let paramSchemaVersion: number;
      try {
        paramSchemaVersion = readParamSchemaVersion(session.character.param_schema_version);
      } catch (error) {
        throw new AvatarLiveError(
          'PARAM_SCHEMA_UNSUPPORTED',
          error instanceof Error ? error.message : String(error),
          true,
        );
      }
      this.paramSchemaVersion = paramSchemaVersion;
      if (!supportsParamSchemaVersion(paramSchemaVersion)) {
        if (!this.allowsAutomaticParamSchemaFallback()) {
          throw this.unsupportedParamSchemaError(paramSchemaVersion, true);
        }
        // Keep only the audio element while the room connects. sessionReady must confirm
        // the exact same version before the existing in-room server fallback is allowed.
        return;
      }
      let asset;
      try {
        asset = await loadCharacterAsset(
          session.character.asset_url,
          session.character.asset_hash,
          signal,
        );
      } catch (error) {
        throw new AvatarLiveError(
          'WEBGL_UNAVAILABLE',
          `客户端角色资产无法安全加载：${error instanceof Error ? error.message : String(error)}`,
          false,
        );
      }
      try {
        assertCharacterAssetParamSchema(asset, paramSchemaVersion);
      } catch (error) {
        throw new AvatarLiveError(
          'PARAM_SCHEMA_MISMATCH',
          error instanceof Error ? error.message : String(error),
          true,
        );
      }
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'avatar-live-canvas';
      this.canvas.setAttribute('aria-label', asset.displayName);
      this.options.container.append(this.canvas);
      try {
        this.renderer = new AvatarRenderer(
          this.canvas,
          asset,
          () => this.handleContextFailure(),
          () => this.handleContextRecovered(),
          (segmentId, durationMs) => this.reportRenderFirstFrame(segmentId, durationMs),
        );
        this.renderer.setAudioClock(() => this.audioElement?.currentTime ?? 0);
        this.renderer.start();
      } catch (error) {
        throw new AvatarLiveError(
          'WEBGL_UNAVAILABLE',
          `客户端渲染初始化失败：${error instanceof Error ? error.message : String(error)}`,
          false,
        );
      }
    } else {
      this.createServerVideoElement();
    }
  }

  private installRoomHandlers(room: Room, generation: number): void {
    const isCurrent = () => room === this.room && generation === this.lifecycleGeneration;
    room.on(RoomEvent.TrackSubscribed, (track) => {
      if (isCurrent()) this.attachTrack(track);
    });
    room.on(RoomEvent.TrackUnsubscribed, (track) => this.detachTrack(track));
    room.on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
      if (isCurrent()) this.handleData(payload, topic);
    });
    room.on(RoomEvent.Disconnected, () => {
      if (
        this.intentionalDisconnects.has(room)
        || this.connectingRooms.has(room)
        || !isCurrent()
      ) return;
      this.handleUnexpectedDisconnect(room, generation);
    });
    room.on(RoomEvent.Reconnecting, () => {
      if (!isCurrent()) return;
      this.stateBeforeReconnect = this.currentState;
      this.setState('connecting');
    });
    room.on(RoomEvent.Reconnected, () => {
      if (!isCurrent()) return;
      const restored = this.pendingServerState ?? this.stateBeforeReconnect;
      this.pendingServerState = undefined;
      this.stateBeforeReconnect = undefined;
      this.setState(restored && !['connecting', 'disconnected'].includes(restored) ? restored : 'idle');
    });
    room.on(RoomEvent.MediaDevicesChanged, () => {
      if (!isCurrent()) return;
      void this.recoverMediaDevices().catch((error) => {
        this.emitError(new AvatarLiveError(
          'UPSTREAM_ERROR',
          `音频设备切换失败：${error instanceof Error ? error.message : String(error)}`,
          false,
        ));
      });
    });
    room.on(RoomEvent.AudioPlaybackStatusChanged, (playing: boolean) => {
      if (isCurrent() && !playing && this.currentState === 'speaking') {
        this.emitError(new AvatarLiveError('AUTOPLAY_BLOCKED', '浏览器阻止了音频播放，请点击页面后重试', false));
      }
    });
  }

  private attachTrack(track: RemoteTrack): void {
    if (track.kind === Track.Kind.Audio && this.audioElement) {
      track.attach(this.audioElement);
      if (track instanceof RemoteAudioTrack) {
        this.remoteAudioTracks.add(track);
        if (this.options.audioOutputDeviceId) {
          void this.setAudioOutputDevice(this.options.audioOutputDeviceId).catch(
            async (error) => {
              try {
                await this.setAudioOutputDevice('default');
              } catch {
                // Preserve the original device-selection error; browsers without
                // output-device selection support will reject both attempts.
              }
              this.emitError(new AvatarLiveError(
                'UPSTREAM_ERROR',
                `无法使用指定的音频输出设备，已尝试切换到系统默认设备：${error instanceof Error ? error.message : String(error)}`,
                false,
              ));
            },
          );
        }
      }
      this.startLipSync(track);
      void this.audioElement.play().catch(() => {
        this.emitError(new AvatarLiveError('AUTOPLAY_BLOCKED', '浏览器阻止了音频播放，请点击开始', false));
      });
    } else if (track.kind === Track.Kind.Video && this.videoElement) {
      track.attach(this.videoElement);
    }
  }

  private detachTrack(track: RemoteTrack): void {
    if (track instanceof RemoteAudioTrack) {
      this.remoteAudioTracks.delete(track);
      this.stopLipSync();
    }
    track.detach();
  }

  private startLipSync(track: RemoteTrack): void {
    const mediaStreamTrack = track.mediaStreamTrack;
    if (!this.renderer || !mediaStreamTrack) return;
    this.stopLipSync();
    try {
      this.lipSync = new AudioLipSync(new MediaStream([mediaStreamTrack]));
    } catch {
      // Without Web Audio the drive timeline still animates the mouth, just less tightly.
      return;
    }
    const lipSync = this.lipSync;
    this.renderer.setMouthSource((emotion) => lipSync.sample(emotion));
    void lipSync.resume().catch(() => undefined);
  }

  private stopLipSync(): void {
    if (!this.lipSync) return;
    const lipSync = this.lipSync;
    this.lipSync = undefined;
    this.renderer?.setMouthSource(undefined);
    void lipSync.close().catch(() => undefined);
  }

  private handleData(payload: Uint8Array, topic?: string): void {
    if (topic === PARAM_TOPIC && this.renderer) {
      // DataChannel topics have independent ordering. Do not decode a drive packet until
      // sessionReady has confirmed that the agent and REST control plane agree on its schema.
      if (!this.sessionReady) return;
      try {
        this.renderer.push(decodeDriveFrame(payload, this.paramSchemaVersion));
      } catch (error) {
        this.emitError(new AvatarLiveError('UPSTREAM_ERROR', String(error), false));
      }
      return;
    }
    if (topic !== EVENT_TOPIC) return;
    try {
      const event = JSON.parse(new TextDecoder().decode(payload)) as Record<string, unknown>;
      this.handleServerEvent(event);
    } catch {
      this.emitError(new AvatarLiveError('UPSTREAM_ERROR', '收到无法解析的服务端事件', false));
    }
  }

  private handleServerEvent(event: Record<string, unknown>): void {
    switch (event.type) {
      case 'stateChange':
        if (AVATAR_STATES.has(event.state as AvatarState)) {
          const state = event.state as AvatarState;
          if (this.sessionReady) this.setState(state);
          else this.pendingServerState = state;
        }
        break;
      case 'userTranscript':
        this.emitter.emit('userTranscript', { text: String(event.text ?? ''), final: Boolean(event.final) });
        break;
      case 'avatarTranscript':
        this.emitter.emit('avatarTranscript', { text: String(event.text ?? ''), final: Boolean(event.final) });
        break;
      case 'interrupted':
        this.emitter.emit('interrupted', { spokenText: String(event.spokenText ?? '') });
        break;
      case 'turnMetrics':
        this.emitter.emit('turnMetrics', event.metrics as unknown as TurnMetrics);
        break;
      case 'degradation': {
        const rawAudio = String(event.audio ?? 'unavailable');
        const audio = DEGRADATION_AUDIO.has(rawAudio as DegradationAudio)
          ? (rawAudio as DegradationAudio)
          : 'unavailable';
        this.emitter.emit('degradation', {
          level: Number(event.level),
          reason: String(event.reason ?? ''),
          audio,
          ...(event.errorType ? { errorType: String(event.errorType) } : {}),
        });
        break;
      }
      case 'status':
        this.emitter.emit('status', {
          state: String(event.state ?? ''),
          message: String(event.message ?? ''),
        });
        break;
      case 'error':
        this.emitError(new AvatarLiveError(String(event.code ?? 'UPSTREAM_ERROR'), String(event.message ?? '服务异常'), Boolean(event.fatal)));
        break;
      case 'audioSegmentStarted':
        this.renderer?.startSegment(this.readSegmentId(event.segmentId));
        break;
      case 'audioSegmentEnded': {
        const audioPtsMs = Number(event.audioPtsMs);
        if (Number.isFinite(audioPtsMs)) this.renderer?.endSegment(audioPtsMs);
        break;
      }
      case 'audioBufferCleared':
        this.renderer?.clearMouth();
        break;
      case 'sessionReady':
        this.resolveSessionReady(
          String(event.sessionId ?? ''),
          event.paramSchemaVersion,
          event.idleSeed,
        );
        break;
      case 'renderModeChanged':
        this.resolveRenderModeChange(
          String(event.requestId ?? ''),
          String(event.renderMode ?? '') as RenderMode,
        );
        break;
      case 'renderModeChangeFailed':
        this.rejectRenderModeChange(
          String(event.requestId ?? ''),
          new AvatarLiveError(
            String(event.code ?? 'UPSTREAM_ERROR'),
            String(event.message ?? '服务端渲染启动失败'),
            false,
          ),
        );
        break;
      case 'interruptionModeChanged':
        this.resolveInterruptionModeChange(
          String(event.requestId ?? ''),
          Boolean(event.enabled),
        );
        break;
      case 'interruptionModeChangeFailed':
        this.rejectInterruptionModeChange(
          String(event.requestId ?? ''),
          new AvatarLiveError(
            String(event.code ?? 'UPSTREAM_ERROR'),
            String(event.message ?? '自动打断设置失败'),
            false,
          ),
        );
        break;
      default:
        break;
    }
  }

  private async sendCommand(command: Record<string, unknown>): Promise<void> {
    if (!this.room) throw this.notConnectedError();
    await this.room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(command)), {
      reliable: true,
      topic: COMMAND_TOPIC,
    });
  }

  private reportRenderFirstFrame(segmentId: number, durationMs: number): void {
    if (!this.room || !Number.isSafeInteger(segmentId) || !Number.isFinite(durationMs)) return;
    const payload = new TextEncoder().encode(JSON.stringify({
      type: 'renderFirstFrame',
      segmentId,
      durationMs: Math.max(0, Math.round(durationMs * 100) / 100),
    }));
    void this.room.localParticipant.publishData(payload, {
      reliable: true,
      topic: CLIENT_METRICS_TOPIC,
    }).catch(() => undefined);
  }

  private readSegmentId(value: unknown): number | undefined {
    const segmentId = Number(value);
    return Number.isSafeInteger(segmentId) && segmentId >= 0 ? segmentId : undefined;
  }

  private resolveRenderMode(requested: RequestedRenderMode): RenderMode {
    if (requested === 'server') return 'server';
    return AvatarRenderer.supported() ? 'client' : 'server';
  }

  private allowsAutomaticParamSchemaFallback(): boolean {
    return (this.options.renderMode ?? 'auto') === 'auto';
  }

  private unsupportedParamSchemaError(version: number, fatal: boolean): AvatarLiveError {
    return new AvatarLiveError(
      'PARAM_SCHEMA_UNSUPPORTED',
      `unsupported param schema version ${version}; supported: ${SUPPORTED_PARAM_SCHEMA_VERSIONS.join(', ')}`,
      fatal,
    );
  }

  private handleContextFailure(): void {
    this.contextFailures += 1;
    this.emitError(new AvatarLiveError('WEBGL_UNAVAILABLE', 'WebGL 上下文已丢失，正在恢复', false));
    if (this.contextFailures >= 2) this.startServerFallback();
    else this.startContextRecoveryTimeout();
  }

  private handleContextRecovered(): void {
    this.cancelContextRecoveryTimeout();
    this.contextFailures = 0;
  }

  private startContextRecoveryTimeout(): void {
    if (this.contextRecoveryTimeout !== undefined || this.fallbackInProgress) return;
    const generation = this.lifecycleGeneration;
    this.contextRecoveryTimeout = setTimeout(() => {
      this.contextRecoveryTimeout = undefined;
      if (
        generation !== this.lifecycleGeneration
        || this.selectedRenderMode !== 'client'
      ) return;
      this.startServerFallback();
    }, WEBGL_CONTEXT_RECOVERY_TIMEOUT_MS);
  }

  private cancelContextRecoveryTimeout(): void {
    if (this.contextRecoveryTimeout === undefined) return;
    clearTimeout(this.contextRecoveryTimeout);
    this.contextRecoveryTimeout = undefined;
  }

  private startServerFallback(): void {
    this.cancelContextRecoveryTimeout();
    if (this.fallbackInProgress) return;
    this.fallbackInProgress = true;
    void this.fallbackToServer().catch((error) => {
      if (!this.isLifecycleCancellation(error)) this.emitError(this.asAvatarError(error));
    });
  }

  private async fallbackToServer(): Promise<void> {
    this.cancelContextRecoveryTimeout();
    const room = this.room;
    const generation = this.lifecycleGeneration;
    if (!room || this.selectedRenderMode !== 'client') {
      this.fallbackInProgress = false;
      return;
    }

    const requestId = `render-${Date.now()}-${++this.renderModeRequestSequence}`;
    const video = this.createServerVideoElement();
    video.hidden = true;
    video.style.display = 'none';
    const changed = this.createRenderModeWaiter(generation, requestId);
    try {
      await this.sendCommand({ type: 'setRenderMode', renderMode: 'server', requestId });
      const renderMode = await changed;
      this.assertCurrentGeneration(generation);
      if (room !== this.room || renderMode !== 'server') throw new LifecycleCancelledError();

      this.selectedRenderMode = 'server';
      this.forcedRenderMode = 'server';
      this.contextFailures = 0;
      this.cancelContextRecoveryTimeout();
      this.renderer?.destroy();
      this.renderer = undefined;
      this.canvas?.remove();
      this.canvas = undefined;
      video.hidden = false;
      video.style.removeProperty('display');
    } catch (error) {
      this.cancelRenderModeChange(this.asAvatarError(error), requestId);
      if (this.selectedRenderMode !== 'server') {
        video.remove();
        if (this.videoElement === video) this.videoElement = undefined;
      }
      throw error;
    } finally {
      this.fallbackInProgress = false;
    }
  }

  private createServerVideoElement(): HTMLVideoElement {
    const video = this.videoElement ?? document.createElement('video');
    video.autoplay = true;
    video.playsInline = true;
    video.className = 'avatar-live-video';
    video.dataset.fit = this.serverVideoFit;
    video.style.objectFit = this.serverVideoFit;
    video.style.objectPosition = 'center center';
    video.style.backgroundColor = this.serverVideoFit === 'contain' ? '#000' : '';
    if (!this.videoElement) {
      this.videoElement = video;
      this.options.container.append(video);
    }
    return video;
  }

  private async recoverMediaDevices(): Promise<void> {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices();
    await this.recoverMissingMicrophone(devices);
    await this.recoverAudioOutput(devices);
  }

  private async recoverMissingMicrophone(devices: MediaDeviceInfo[]): Promise<void> {
    if (!this.microphoneStarted || !this.room) return;
    const inputs = devices.filter((device) => device.kind === 'audioinput');
    if (this.microphoneDeviceId && inputs.some((device) => device.deviceId === this.microphoneDeviceId)) return;
    if (inputs.length === 0) {
      this.stopMic();
      this.emitError(new AvatarLiveError('MIC_DENIED', '麦克风设备已断开', false));
      return;
    }
    await this.room.localParticipant.setMicrophoneEnabled(true, {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
    });
    this.microphoneDeviceId = undefined;
  }

  private async recoverAudioOutput(devices: MediaDeviceInfo[]): Promise<void> {
    const requested = this.options.audioOutputDeviceId;
    if (!requested || this.remoteAudioTracks.size === 0) return;
    const outputs = devices.filter((device) => device.kind === 'audiooutput');
    const available = outputs.some((device) => device.deviceId === requested);
    const fallback = outputs.some((device) => device.deviceId === 'default') ? 'default' : '';
    const target = available ? requested : fallback;
    if (target === this.activeAudioOutputDeviceId) return;
    await this.setAudioOutputDevice(target);
    if (!available) {
      this.emitError(new AvatarLiveError(
        'UPSTREAM_ERROR',
        '指定的音频输出设备已断开，已切换到系统默认设备',
        false,
      ));
    }
  }

  private async setAudioOutputDevice(deviceId: string): Promise<void> {
    if (!this.room || this.remoteAudioTracks.size === 0) return;
    // webAudioMix routes audible output through the Room's AudioContext and keeps
    // attached HTMLAudioElements muted. Switching only the elements' sink IDs would
    // therefore leave the sound on the old device. Use LiveKit's room-level API so
    // both the AudioContext destination and attached elements move together.
    const switched = await this.room.switchActiveDevice('audiooutput', deviceId);
    if (!switched) throw new Error(`audio output device switch was rejected: ${deviceId}`);
    this.activeAudioOutputDeviceId = deviceId;
  }

  private async disconnectInternal(
    reason: SessionEndReason = 'user',
    alreadyDisconnectedRoom?: Room,
  ): Promise<void> {
    this.cancelContextRecoveryTimeout();
    this.cancelSessionReady(new LifecycleCancelledError());
    this.cancelRenderModeChange(new LifecycleCancelledError());
    this.sessionReady = false;
    this.paramSchemaVersion = LEGACY_PARAM_SCHEMA_VERSION;
    this.pendingServerState = undefined;
    this.stateBeforeReconnect = undefined;
    this.microphoneStarted = false;
    this.microphoneDeviceId = undefined;
    this.remoteAudioTracks.clear();
    this.activeAudioOutputDeviceId = undefined;
    this.clearPreparedElements();
    const room = this.room;
    this.room = undefined;
    const intentional = room !== undefined && room !== alreadyDisconnectedRoom;
    if (intentional) this.intentionalDisconnects.add(room);

    // Mark the control-plane session ended before deleting/disconnecting the room. This keeps
    // room-finished webhooks from classifying a normal SDK shutdown as an unexpected drop.
    await this.releaseSessions(reason);
    if (room) {
      if (intentional) {
        try {
          await room.disconnect();
        } catch (error) {
          this.emitError(new AvatarLiveError('UPSTREAM_ERROR', `断开 LiveKit 会话失败：${String(error)}`, false));
        }
      }
      room.removeAllListeners();
    }
  }

  private clearPreparedElements(): void {
    this.stopLipSync();
    this.renderer?.destroy();
    this.renderer = undefined;
    this.canvas?.remove();
    this.canvas = undefined;
    this.audioElement?.remove();
    this.videoElement?.remove();
    this.audioElement = undefined;
    this.videoElement = undefined;
  }

  private handleUnexpectedDisconnect(room: Room, generation: number): void {
    if (room !== this.room || generation !== this.lifecycleGeneration) return;
    ++this.lifecycleGeneration;
    this.connectAbortController?.abort();
    this.cancelSessionReady(new LifecycleCancelledError());
    this.cancelInterruptionModeChange(new LifecycleCancelledError());
    const pendingConnect = this.connectPromise;
    const promise = (async () => {
      await Promise.resolve();
      const cleanup = this.disconnectInternal('unexpected_disconnect', room);
      this.setState('disconnected');
      await cleanup;
      await pendingConnect?.catch(() => undefined);
      await this.disconnectInternal('unexpected_disconnect', room);
    })();
    this.disconnectPromise = promise;
    void promise.finally(() => {
      if (this.disconnectPromise === promise) this.disconnectPromise = undefined;
    });
  }

  private createSessionReadyWaiter(
    generation: number,
    sessionId: string,
    paramSchemaVersion?: number,
  ): Promise<void> {
    this.cancelSessionReady(new LifecycleCancelledError());
    let resolve!: () => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<void>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise;
      reject = rejectPromise;
    });
    void promise.catch(() => undefined);
    const timeout = setTimeout(() => {
      if (this.readyWaiter?.generation !== generation) return;
      this.readyWaiter = undefined;
      reject(new AvatarLiveError('UPSTREAM_ERROR', '数字人服务准备超时', false));
    }, SESSION_READY_TIMEOUT_MS);
    this.readyWaiter = {
      generation,
      sessionId,
      paramSchemaVersion,
      promise,
      resolve,
      reject,
      timeout,
    };
    return promise;
  }

  private resolveSessionReady(
    sessionId: string,
    rawParamSchemaVersion: unknown,
    rawIdleSeed: unknown,
  ): void {
    const waiter = this.readyWaiter;
    if (!waiter || waiter.generation !== this.lifecycleGeneration) return;
    if (sessionId !== waiter.sessionId) {
      this.failSessionReady(waiter, new AvatarLiveError(
        'UPSTREAM_ERROR',
        '收到缺失或不匹配的会话就绪事件',
        false,
      ));
      return;
    }
    if (waiter.paramSchemaVersion !== undefined) {
      let receivedVersion: number;
      try {
        // Missing means an older agent. Only the historical face[8] schema 1 may use
        // this fallback; a future REST schema therefore cannot silently pair with it.
        receivedVersion = readParamSchemaVersion(rawParamSchemaVersion);
      } catch (error) {
        this.failSessionReady(waiter, new AvatarLiveError(
          'PARAM_SCHEMA_UNSUPPORTED',
          error instanceof Error ? error.message : String(error),
          true,
        ));
        return;
      }
      if (receivedVersion !== waiter.paramSchemaVersion) {
        this.failSessionReady(waiter, new AvatarLiveError(
          'PARAM_SCHEMA_MISMATCH',
          `parameter schema mismatch: session=${waiter.paramSchemaVersion}, agent=${receivedVersion}`,
          true,
        ));
        return;
      }
      if (!supportsParamSchemaVersion(receivedVersion)) {
        if (!this.allowsAutomaticParamSchemaFallback()) {
          this.failSessionReady(
            waiter,
            this.unsupportedParamSchemaError(receivedVersion, true),
          );
          return;
        }
        this.startParamSchemaFallback(waiter, receivedVersion);
        return;
      }
    }
    const idleSeed = rawIdleSeed === undefined ? 0 : Number(rawIdleSeed);
    if (!Number.isSafeInteger(idleSeed) || idleSeed < 0 || idleSeed > 0xffff_ffff) {
      this.failSessionReady(waiter, new AvatarLiveError(
        'UPSTREAM_ERROR',
        '收到无效的待机动画种子',
        false,
      ));
      return;
    }
    this.renderer?.setIdleSeed(idleSeed);
    this.completeSessionReady(waiter);
  }

  private completeSessionReady(waiter: SessionReadyWaiter): void {
    if (waiter.generation !== this.lifecycleGeneration) {
      this.failSessionReady(waiter, new LifecycleCancelledError());
      return;
    }
    clearTimeout(waiter.timeout);
    if (this.readyWaiter === waiter) this.readyWaiter = undefined;
    this.sessionReady = true;
    if (this.pendingServerState) {
      const state = this.pendingServerState;
      this.pendingServerState = undefined;
      this.setState(state);
    }
    waiter.resolve();
  }

  private failSessionReady(waiter: SessionReadyWaiter, error: Error): void {
    clearTimeout(waiter.timeout);
    if (this.readyWaiter === waiter) this.readyWaiter = undefined;
    waiter.reject(error);
  }

  private startParamSchemaFallback(waiter: SessionReadyWaiter, version: number): void {
    clearTimeout(waiter.timeout);
    if (this.readyWaiter === waiter) this.readyWaiter = undefined;
    this.emitError(this.unsupportedParamSchemaError(version, false));
    this.fallbackInProgress = true;
    void (async () => {
      try {
        await this.fallbackToServer();
        this.assertCurrentGeneration(waiter.generation);
        if (this.selectedRenderMode !== 'server') throw new LifecycleCancelledError();
        this.completeSessionReady(waiter);
      } catch (error) {
        this.failSessionReady(waiter, this.asAvatarError(error));
      }
    })();
  }

  private cancelSessionReady(error: Error): void {
    const waiter = this.readyWaiter;
    if (!waiter) return;
    clearTimeout(waiter.timeout);
    this.readyWaiter = undefined;
    waiter.reject(error);
  }

  private createRenderModeWaiter(generation: number, requestId: string): Promise<RenderMode> {
    this.cancelRenderModeChange(new LifecycleCancelledError());
    let resolve!: (renderMode: RenderMode) => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<RenderMode>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise;
      reject = rejectPromise;
    });
    void promise.catch(() => undefined);
    const timeout = setTimeout(() => {
      if (this.renderModeWaiter?.requestId !== requestId) return;
      this.renderModeWaiter = undefined;
      reject(new AvatarLiveError('UPSTREAM_ERROR', '切换服务端渲染超时', false));
    }, RENDER_MODE_CHANGE_TIMEOUT_MS);
    this.renderModeWaiter = { generation, requestId, promise, resolve, reject, timeout };
    return promise;
  }

  private resolveRenderModeChange(requestId: string, renderMode: RenderMode): void {
    const waiter = this.renderModeWaiter;
    if (
      !waiter
      || waiter.generation !== this.lifecycleGeneration
      || waiter.requestId !== requestId
    ) return;
    clearTimeout(waiter.timeout);
    this.renderModeWaiter = undefined;
    if (renderMode !== 'server') {
      waiter.reject(new AvatarLiveError('UPSTREAM_ERROR', '服务端返回了无效渲染模式', false));
      return;
    }
    waiter.resolve(renderMode);
  }

  private rejectRenderModeChange(requestId: string, error: Error): void {
    const waiter = this.renderModeWaiter;
    if (!waiter || waiter.requestId !== requestId) return;
    clearTimeout(waiter.timeout);
    this.renderModeWaiter = undefined;
    waiter.reject(error);
  }

  private cancelRenderModeChange(error: Error, requestId?: string): void {
    const waiter = this.renderModeWaiter;
    if (!waiter || (requestId !== undefined && waiter.requestId !== requestId)) return;
    clearTimeout(waiter.timeout);
    this.renderModeWaiter = undefined;
    waiter.reject(error);
  }

  private createInterruptionModeWaiter(
    generation: number,
    requestId: string,
    enabled: boolean,
  ): Promise<void> {
    this.cancelInterruptionModeChange(new LifecycleCancelledError());
    let resolve!: () => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<void>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise;
      reject = rejectPromise;
    });
    void promise.catch(() => undefined);
    const timeout = setTimeout(() => {
      if (this.interruptionModeWaiter?.requestId !== requestId) return;
      this.interruptionModeWaiter = undefined;
      reject(new AvatarLiveError('UPSTREAM_ERROR', '自动打断设置超时', false));
    }, INTERRUPTION_MODE_CHANGE_TIMEOUT_MS);
    this.interruptionModeWaiter = {
      generation,
      requestId,
      enabled,
      promise,
      resolve,
      reject,
      timeout,
    };
    return promise;
  }

  private resolveInterruptionModeChange(requestId: string, enabled: boolean): void {
    const waiter = this.interruptionModeWaiter;
    if (
      !waiter
      || waiter.generation !== this.lifecycleGeneration
      || waiter.requestId !== requestId
    ) return;
    clearTimeout(waiter.timeout);
    this.interruptionModeWaiter = undefined;
    if (enabled !== waiter.enabled) {
      waiter.reject(new AvatarLiveError('UPSTREAM_ERROR', '服务端返回了无效的自动打断设置', false));
      return;
    }
    waiter.resolve();
  }

  private rejectInterruptionModeChange(requestId: string, error: Error): void {
    const waiter = this.interruptionModeWaiter;
    if (!waiter || waiter.requestId !== requestId) return;
    clearTimeout(waiter.timeout);
    this.interruptionModeWaiter = undefined;
    waiter.reject(error);
  }

  private cancelInterruptionModeChange(error: Error, requestId?: string): void {
    const waiter = this.interruptionModeWaiter;
    if (!waiter || (requestId !== undefined && waiter.requestId !== requestId)) return;
    clearTimeout(waiter.timeout);
    this.interruptionModeWaiter = undefined;
    waiter.reject(error);
  }

  private async releaseSessions(reason: SessionEndReason): Promise<void> {
    const results = await Promise.allSettled(
      [...this.sessions.values()].map((session) => this.releaseSession(session, reason)),
    );
    for (const result of results) {
      if (result.status === 'rejected') {
        this.emitError(new AvatarLiveError('UPSTREAM_ERROR', `释放会话失败：${String(result.reason)}`, false));
      }
    }
  }

  private releaseSession(session: SessionResponse, reason: SessionEndReason): Promise<void> {
    const existing = this.sessionDeletePromises.get(session.session_id);
    if (existing) return existing;
    const promise = (async () => {
      const response = await fetch(this.sessionDeleteUrl(session.session_id, reason), {
        method: 'DELETE',
        keepalive: true,
        credentials: 'same-origin',
        headers: this.options.sessionHeaders,
      });
      if (!response.ok && response.status !== 404 && response.status !== 410) {
        throw new Error(`DELETE session failed: ${response.status}`);
      }
      this.sessions.delete(session.session_id);
    })();
    this.sessionDeletePromises.set(session.session_id, promise);
    void promise.then(
      () => this.sessionDeletePromises.delete(session.session_id),
      () => this.sessionDeletePromises.delete(session.session_id),
    );
    return promise;
  }

  private sessionDeleteUrl(sessionId: string, reason: SessionEndReason): string {
    const base = this.options.sessionDeleteEndpoint ?? this.options.sessionEndpoint;
    const url = new URL(base, document.baseURI);
    url.pathname = `${url.pathname.replace(/\/$/, '')}/${encodeURIComponent(sessionId)}`;
    url.searchParams.set('reason', reason);
    return url.toString();
  }

  private assertCurrentGeneration(generation: number): void {
    if (generation !== this.lifecycleGeneration) throw new LifecycleCancelledError();
  }

  private isLifecycleCancellation(error: unknown): boolean {
    return error instanceof LifecycleCancelledError
      || (error instanceof DOMException && error.name === 'AbortError')
      || (error instanceof Error && error.name === 'AbortError');
  }

  private setState(state: AvatarState): void {
    if (state === this.currentState) return;
    const previous = this.currentState;
    this.currentState = state;
    this.emitter.emit('stateChange', state, previous);
  }

  private requireUserActivation(): void {
    const activation = navigator.userActivation;
    if (activation && !activation.isActive) {
      throw new AvatarLiveError('AUTOPLAY_BLOCKED', '请通过点击或触摸操作开始会话', false);
    }
  }

  private rememberSessionExpiry(session: SessionResponse): void {
    const expiresAtMs = Date.parse(session.expires_at);
    this.lastSessionExpiresAtMs = Number.isFinite(expiresAtMs) ? expiresAtMs : undefined;
  }

  private notConnectedError(): AvatarLiveError {
    if (
      this.lastSessionExpiresAtMs !== undefined
      && Date.now() >= this.lastSessionExpiresAtMs
    ) {
      return new AvatarLiveError('SESSION_EXPIRED', '会话 token 已过期，请重新连接', true);
    }
    return new AvatarLiveError('NOT_CONNECTED', '会话尚未连接', false);
  }

  private emitError(error: AvatarLiveError): void {
    this.emitter.emit('error', { code: error.code, message: error.message, fatal: error.fatal });
  }

  private asAvatarError(error: unknown): AvatarLiveError {
    if (error instanceof AvatarLiveError) return error;
    return new AvatarLiveError('UPSTREAM_ERROR', error instanceof Error ? error.message : String(error), false);
  }
}
